import { useConfig } from '../context/ConfigContext';
import { formatMoney } from '../utils/currencyUtil';
import '../css/Pos.css';

/**
 * The answered customization options and extras of one order line, ONE PER
 * LINE, each priced when it costs something:
 *
 *   Base price: ₱80
 *   Sugar Level: 50%
 *   Toppings: Coffee Jelly +₱10
 *   Extra: Pearl × 2 +₱20
 *
 * Stacked rather than run together, because whoever makes the drink reads
 * them as a checklist. The prices are there so the customer can see how the
 * unit price beside the line was reached — the base, then each addition — and
 * the base price line only appears when something was actually added, so a
 * line with free answers (a sugar level) stays as short as before.
 *
 * Spans throughout (shown as blocks by CSS), so it can sit inside a table
 * cell on the receipt or inside the order rail's inline name block.
 *
 * @param {object} props
 * @param {Array<{label: string, value: string, price?: number}>|null} [props.options] -
 *   from resolvePosSelections, or an order item's stored `options`. `price` is
 *   what that answer adds to ONE item.
 * @param {number|null} [props.basePrice] - the product's own price for one,
 *   before any additions; omitted when not known (older orders)
 * @param {string} [props.className] - extra class for where it is placed
 * @returns {JSX.Element|null} nothing when the line has no options
 */
function PosSelectedOptions({ options, basePrice = null, className = '' }) {
  const { config } = useConfig();
  const list = Array.isArray(options) ? options.filter((option) => option?.label && option?.value) : [];
  if (list.length === 0) return null;

  const priced = list.some((option) => Number(option.price) > 0);
  const showBase = priced && basePrice !== null && basePrice !== undefined && Number.isFinite(Number(basePrice));

  return (
    <span className={`pos-selected-opts${className ? ` ${className}` : ''}`}>
      {showBase && (
        <span className="pos-selected-opt">
          <span className="pos-selected-opt-label">Base price:</span> {formatMoney(Number(basePrice), config)}
        </span>
      )}
      {/* Index in the key: several extras share the label "Extra". */}
      {list.map((option, index) => (
        <span key={`${option.label}-${index}`} className="pos-selected-opt">
          <span className="pos-selected-opt-label">{option.label}:</span> {option.value}
          {Number(option.price) > 0 && (
            <span className="pos-selected-opt-price"> +{formatMoney(Number(option.price), config)}</span>
          )}
        </span>
      ))}
    </span>
  );
}

export default PosSelectedOptions;
